import React, { useState } from "react";
import { useClinicData } from "../../context/ClinicDataContext";
import Modal from "../Modal/Modal";
import PhoneInput from "../PhoneInput/PhoneInput";
import { dobFromIC, formatICInput } from "../../utils/format";
import { DEFAULT_COUNTRY_CODE, hasNumber, joinPhone, splitPhone } from "../../utils/phone";

// Patient registration form, shared by the Patients page and the quick registration at Checkout.

const EMPTY_FORM = { name: "", phone: DEFAULT_COUNTRY_CODE, ic: "", dob: "", gender: "Female", email: "", alerts: "" };

// Turns what the front desk already typed in a patient search into a head start on the form:
// an IC number (YYMMDD-PB-####), a phone number, or otherwise a name.
const prefillFrom = (query, today) => {
  const q = (query || "").trim();
  if (!q) return {};
  const d = q.replace(/\D/g, "");
  if (/^[\d\s+()-]+$/.test(q) && d.length >= 6) {
    const looksLikeIC = /^\d{6}-?\d{2}-?\d{4}$/.test(q) || (d.length === 12 && !q.startsWith("+") && !d.startsWith("0") && !d.startsWith("60"));
    if (looksLikeIC) {
      const ic = formatICInput(d);
      return { ic, dob: dobFromIC(ic, today) || "" };
    }
    if (q.startsWith("+")) {
      const { code, number } = splitPhone(q);
      return { phone: joinPhone(code, number) };
    }
    return { phone: joinPhone(DEFAULT_COUNTRY_CODE, d.startsWith("60") ? d.slice(2) : d) };
  }
  return { name: q };
};

const NewPatientModal = ({ onClose, onSaved, initialQuery = "", title = "New patient", saveLabel = "Save patient" }) => {
  const { addPatient, today } = useClinicData();
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, ...prefillFrom(initialQuery, today) }));
  const [error, setError] = useState("");
  const update = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setError("");
  };

  const handleSave = () => {
    if (!form.name.trim() || !hasNumber(form.phone) || !form.dob) {
      setError("Name, mobile number and date of birth are required.");
      return;
    }
    const nationalDigits = splitPhone(form.phone).number.replace(/\D/g, "").replace(/^0+/, "");
    if (nationalDigits.length < 7 || nationalDigits.length > 12) {
      setError("Check the mobile number. It should be 7 to 12 digits after the country code.");
      return;
    }
    const { code, number } = splitPhone(form.phone);
    const patient = addPatient({
      ...form,
      phone: joinPhone(code, number),
      name: form.name.trim(),
      alerts: form.alerts
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    });
    onSaved(patient);
    onClose();
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn--secondary" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" type="button" onClick={handleSave}>
            {saveLabel}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label className="form-field form-grid__full">
          <span className="form-field__label">Full name (as per IC)</span>
          <input className="form-field__control" value={form.name} onChange={update("name")} />
        </label>
        <div className="form-grid__full">
          <PhoneInput
            label="Mobile number"
            value={form.phone}
            onChange={(phone) => {
              setForm((f) => ({ ...f, phone }));
              setError("");
            }}
          />
        </div>
        <label className="form-field">
          <span className="form-field__label">IC / passport no.</span>
          <input
            className="form-field__control"
            value={form.ic}
            inputMode="numeric"
            placeholder="YYMMDD-PB-####"
            onChange={(e) => {
              const ic = formatICInput(e.target.value);
              const dob = dobFromIC(ic, today);
              setForm((f) => ({ ...f, ic, dob: ic.replace(/\D/g, "").length === 12 && dob ? dob : f.dob }));
              setError("");
            }}
          />
          <span className="form-field__hint">Date of birth fills in from the IC.</span>
        </label>
        <label className="form-field">
          <span className="form-field__label">Date of birth</span>
          <input className="form-field__control" type="date" value={form.dob} onChange={update("dob")} />
        </label>
        <label className="form-field">
          <span className="form-field__label">Gender</span>
          <select className="form-field__control" value={form.gender} onChange={update("gender")}>
            <option>Female</option>
            <option>Male</option>
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Email (optional)</span>
          <input className="form-field__control" type="email" value={form.email} onChange={update("email")} />
        </label>
        <label className="form-field form-grid__full">
          <span className="form-field__label">Medical alerts (optional)</span>
          <input
            className="form-field__control"
            value={form.alerts}
            onChange={update("alerts")}
            placeholder="e.g. Penicillin allergy, Diabetes"
          />
          <span className="form-field__hint">Separate each alert with a comma. These show on every booking.</span>
        </label>
      </div>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
};

export default NewPatientModal;
